import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Grid, Typography } from '@material-ui/core';
import { pathOr } from 'ramda';

import { GUIDE_TYPE } from 'yeself-common/ModelConstant';

import LocalizedPlainStringField from 'yeself-client-common/form/components/LocalizedPlainStringField';

import { selectFallbackLanguage } from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';
import EditGuideFormGeneralTabs from 'Console/components/applications/detail/guides/EditGuideFormGeneralTabs';

import { selectContentLanguage } from 'Console/data/app/selectors';
import {
    makeSelectGraphSandbox,
    makeSelectShouldGuideStartAutomatically,
} from 'Console/data/applicationDetail/guides/selectors';
import { validateDomain } from 'Console/utils/formUtils';

const styles = (theme) => ({
    editGuideFormWrapper: {
        marginTop: theme.typography.pxToRem(10),
        marginBottom: theme.typography.pxToRem(30),
        position: 'relative',
    },
    buttonsWrapper: {
        marginTop: theme.typography.pxToRem(36),
    },
    formLabel: {
        flexBasis: '23%',
        marginRight: theme.typography.pxToRem(42),
        fontSize: theme.typography.pxToRem(14),
        [theme.breakpoints.down('sm')]: {
            'margin-right': 0,
        },
    },
    items: {
        flexWrap: 'nowrap',
        paddingBottom: theme.typography.pxToRem(20),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            'justify-content': 'flex-start',
            'margin-right': 0,
            paddingBottom: theme.typography.pxToRem(20),
        },
    },
    formHeader: {
        marginBottom: theme.typography.pxToRem(45),
    },
    formInput: {
        width: '47%',
        [theme.breakpoints.between('xs', 'md')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
});

const EditGuideForm = ({ classes, form, intl, guide, title }) => {
    const [tabOpened, setTabOpened] = useState(0);

    const selectGraphSandbox = useMemo(makeSelectGraphSandbox, []);
    const selectShouldGuideStartAutomatically = useMemo(makeSelectShouldGuideStartAutomatically, []);

    const contentLanguage = useSelector(selectContentLanguage);
    const fallbackLanguage = useSelector(selectFallbackLanguage);
    const invertedSandboxValue = !useSelector((state) => selectGraphSandbox(state, guide.id));
    const startAutomatically = useSelector((state) =>
        selectShouldGuideStartAutomatically(state, guide.id)
    );

    const { getFieldDecorator } = form;

    const onTabChange = useCallback((event, tabOpened) => setTabOpened(tabOpened), []);

    return (
        <Grid id="yeself-edit-guide-wrapper" container className={classes.editGuideFormWrapper}>
            <Grid
                item
                direction="row"
                alignItems="center"
                justify="space-between"
                className={classes.formHeader}
            >
                {title && (
                    <Grid item>
                        <Typography variant="subtitle1">{title}</Typography>
                    </Grid>
                )}
            </Grid>

            <Grid container item>
                <Grid container item direction="row" alignItems="center" className={classes.items}>
                    <Typography className={classes.formLabel} variant="body1">
                        <FormattedMessage id="Guides.editForm.name" defaultMessage="Guide name" />
                    </Typography>

                    <Grid item className={classes.formInput}>
                        {getFieldDecorator('name', {
                            initialValue: guide.name,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={intl.formatMessage({
                                    id: 'Guides.editForm.name',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid container item direction="row" alignItems="center" className={classes.items}>
                    <Typography className={classes.formLabel} variant="body1">
                        <FormattedMessage
                            id="Guides.editForm.initialUrl"
                            defaultMessage="Initial URL of the webpage"
                        />
                    </Typography>

                    <Grid item className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            form={form}
                            type="text"
                            initialValue={pathOr('', ['initialUrl'], guide)}
                            formItemKey="initialUrl"
                            placeholder={intl.formatMessage({
                                id: 'Guides.editForm.initialUrl',
                            })}
                            additionalRules={[
                                {
                                    validator: validateDomain,
                                },
                            ]}
                        />
                    </Grid>
                </Grid>

                <Grid container item direction="row" alignItems="center" className={classes.items}>
                    <Typography className={classes.formLabel} variant="body1">
                        <FormattedMessage
                            id="Guides.editForm.description"
                            defaultMessage="Description of the guide"
                        />
                    </Typography>

                    <Grid item className={classes.formInput}>
                        {getFieldDecorator('description', {
                            initialValue: guide.description,
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={intl.formatMessage({
                                    id: 'Guides.editForm.description',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {guide.type !== GUIDE_TYPE.RECORDING ? (
                <EditGuideFormGeneralTabs
                    tabOpened={tabOpened}
                    onTabChange={onTabChange}
                    form={form}
                    guide={guide}
                    initialValues={{
                        sandbox: invertedSandboxValue,
                        startAutomatically,
                    }}
                />
            ) : null}
        </Grid>
    );
};

export default withStyles(styles)(EditGuideForm);
